import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { useState, useCallback, useEffect } from "react";
import { Input } from "../../../../components/ui/input";
import { Switch } from "../../../../components/ui/switch";
import clsx from "clsx";
import SelectComponent from "../../../../components/select";
import { RoleMemberProject } from "../../../../constants/user";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import toastifyInstance from "../../../../utils/toastify";
import { updateUser } from "../../../../api/user";
import { useQueryClient } from "@tanstack/react-query";

const positionOptions = Object.values(RoleMemberProject).map(role => ({
    label: role,
    value: role
}));

const validationSchema = yup.object().shape({
    name: yup.string().min(2, "Name must be at least 2 characters").required("Name is required"),
    email: yup.string().email("Invalid email format").required("Email is required"),
    phoneNumber: yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .matches(/^\d{10}$/, "Phone number must be 10 digits")
        .optional(),
    address: yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .optional(),
    birthDate: yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .optional(),
    position: yup.string().required("Position is required"),
    slackId: yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .optional(),
    joinDate: yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .optional(),
    allowedRemote: yup.number()
        .nullable()
        .transform((value) => (value === "" ? 2 : value))
        .optional(),
    screenshotIntervalMinutes: yup.number()
        .nullable()
        .transform((value) => (value === "" ? 5 : value))
        .optional(),
    dailyTrackingTimeLimit: yup.number()
        .nullable()
        .transform((value) => (value === "" ? 450 : value))
        .optional(),
    password: yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .min(6, "Password must be at least 6 characters")
        .optional()
});

function UpdateUser({ data }: { data: any }) {

    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const queryClient = useQueryClient();

    // create refetch function
    const refetch = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['users'] });
    }, [queryClient])

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            name: data.name,
            email: data.email,
            phoneNumber: data.phoneNumber || '',
            address: data.address || '',
            birthDate: data.birthDate || '',
            position: data.position || '',
            slackId: data.slackId || '',
            joinDate: data.joinDate || '',
            allowedRemote: data.allowedRemote || 0,
            screenshotIntervalMinutes: data.screenshotIntervalMinutes || 5,
            dailyTrackingTimeLimit: data.dailyTrackingTimeLimit || 450,
            password: ''
        },
        mode: "onTouched"
    });

    useEffect(() => {
        if (!open) {
            reset({
                name: data.name,
                email: data.email,
                phoneNumber: data.phoneNumber || '',
                address: data.address || '',
                birthDate: data.birthDate || '',
                position: data.position || '',
                slackId: data.slackId || '',
                joinDate: data.joinDate || '',
                allowedRemote: data.allowedRemote || 0,
                screenshotIntervalMinutes: data.screenshotIntervalMinutes || 5,
                dailyTrackingTimeLimit: data.dailyTrackingTimeLimit || 450,
                password: ''
            });
            setPermissions({
                allowedOT: data.allowedOT,
                allowedWorkAtWeekend: data.allowedWorkAtWeekend,
                allowedWorkAtNoonTime: data.allowedWorkAtNoonTime,
                allowedWebTracking: data.allowedWebTracking,
                allowedManualLog: data.allowedManualLog,
                deactivated: data.deactivated,
                allowedWorkOnHoliday: data.allowedWorkOnHoliday || false,
                requiredAfternoonCheckIn: data.requiredAfternoonCheckIn !== undefined ? data.requiredAfternoonCheckIn : true,
                skipCheckAutoClick: data.skipCheckAutoClick || false,
                skipActivityCheck: data.skipActivityCheck || false,
                skipIdenticalImagesCheck: data.skipIdenticalImagesCheck || false,
                skipHeartbeatCheck: data.skipHeartbeatCheck || false
            });
        }
    }, [open, data, reset]);

    const [permissions, setPermissions] = useState<{
        allowedOT: boolean;
        allowedWorkAtWeekend: boolean;
        allowedWorkAtNoonTime: boolean;
        allowedWebTracking: boolean;
        allowedManualLog: boolean;
        deactivated: boolean;
        allowedWorkOnHoliday: boolean;
        requiredAfternoonCheckIn: boolean;
        skipCheckAutoClick: boolean;
        skipActivityCheck: boolean;
        skipIdenticalImagesCheck: boolean;
        skipHeartbeatCheck: boolean;
    }>({
        allowedOT: data.allowedOT,
        allowedWorkAtWeekend: data.allowedWorkAtWeekend,
        allowedWorkAtNoonTime: data.allowedWorkAtNoonTime,
        allowedWebTracking: data.allowedWebTracking,
        allowedManualLog: data.allowedManualLog,
        deactivated: data.deactivated,
        allowedWorkOnHoliday: data.allowedWorkOnHoliday || false,
        requiredAfternoonCheckIn: data.requiredAfternoonCheckIn !== undefined ? data.requiredAfternoonCheckIn : true,
        skipCheckAutoClick: data.skipCheckAutoClick || false,
        skipActivityCheck: data.skipActivityCheck || false,
        skipIdenticalImagesCheck: data.skipIdenticalImagesCheck || false,
        skipHeartbeatCheck: data.skipHeartbeatCheck || false
    });

    const handlePermissionChange = useCallback((key: keyof typeof permissions) => {
        setPermissions(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    }, []);

    const onSubmit = async (newData: any) => {
        try {
            setIsLoading(true);
            const updateData: any = {
                ...newData,
                ...permissions,
                _id: data._id
            };
            // Only include password if it's provided (not empty)
            if (!updateData.password || updateData.password.trim() === '') {
                delete updateData.password;
            }
            await updateUser(updateData);
            toastifyInstance.success("User updated successfully");
            refetch();
        } catch (error) {
            console.error(error);
            toastifyInstance.error("Failed to update user");
        } finally {
            setIsLoading(false);
            setOpen(false);
        }
    };

    if (isLoading) {
        return <></>;
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline">Update</Button>
            </DialogTrigger>
            <DialogContent className="bg-white w-[90%] max-w-[100%]">
                <DialogHeader>
                    <DialogTitle>Update User Information</DialogTitle>
                </DialogHeader>
                <DialogDescription>
                    <form onSubmit={handleSubmit(onSubmit)} className="overflow-y-auto max-h-[80vh] scrollbar-hide">
                        <div className="flex gap-2 my-4 flex-wrap justify-between">
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Full Name</p>
                                <Input
                                    {...register("name")}
                                    required={!!data?.name}
                                />
                                {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
                            </div>
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Email Address</p>
                                <Input
                                    {...register("email")}
                                    required={!!data?.email}
                                />
                                {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
                            </div>
                        </div>

                        <div className="flex gap-2 my-4 justify-between flex-wrap">
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Phone Number</p>
                                <Input
                                    type="text"
                                    placeholder="Enter phone number"
                                    required={!!data?.phoneNumber}
                                    {...register("phoneNumber")}
                                />
                            </div>
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Address</p>
                                <Input
                                    type="text"
                                    placeholder="Enter address"
                                    required={!!data?.address}
                                    {...register("address")}
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 my-4 justify-between flex-wrap">
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Birth Date</p>
                                <Input type="date" placeholder="Select birth date" {...register("birthDate")} />
                            </div>
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Position</p>
                                <SelectComponent
                                    options={positionOptions}
                                    defaultValue={data?.position?.toLowerCase()}
                                    onChange={(value) => setValue("position", value)}
                                    placeholder="Select position"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 my-4 justify-between flex-wrap">
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Slack ID</p>
                                <Input
                                    type="text"
                                    placeholder="Enter Slack ID"
                                    {...register("slackId")}
                                />
                            </div>
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Join Date</p>
                                <Input
                                    type="date"
                                    placeholder="Select join date"
                                    {...register("joinDate")}
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 my-4 justify-between flex-wrap">
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Allowed Remote Days</p>
                                <Input
                                    type="number"
                                    placeholder="Enter number of remote days"
                                    {...register("allowedRemote")}
                                    min="0"
                                />
                            </div>
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Screenshot Interval</p>
                                <Input
                                    type="number"
                                    placeholder="Enter screenshot interval"
                                    {...register("screenshotIntervalMinutes")}
                                />
                            </div>
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">Daily Tracking Time Limit (minutes)</p>
                                <Input
                                    type="number"
                                    placeholder="Enter daily tracking time limit"
                                    {...register("dailyTrackingTimeLimit")}
                                    min="0"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 my-4 justify-between flex-wrap">
                            <div className={clsx("w-[45%] min-w-[300px]")}>
                                <p className="text-sm font-medium">New Password</p>
                                <Input
                                    type="password"
                                    placeholder="Leave empty to keep current password"
                                    autoComplete="off"
                                    {...register("password")}
                                />
                                {errors.password && <p className="text-red-500 text-sm">{errors.password.message}</p>}
                                <p className="text-xs text-muted-foreground mt-1">
                                    Enter a new password to change it. Leave empty to keep the current password.
                                </p>
                            </div>
                        </div>

                        <div className="my-4">
                            <p className="text-sm font-medium mb-4">Permissions</p>
                            <div className="flex gap-10 flex-wrap">
                                {Object.entries(permissions).map(([key, value]) => (
                                    <div key={key} className="gap-2">
                                        <p className="text-sm font-medium whitespace-nowrap">
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                        </p>
                                        <Switch
                                            className="bg-primary"
                                            checked={value}
                                            onCheckedChange={() => handlePermissionChange(key as keyof typeof permissions)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Button type="submit" className="mt-4">Update User</Button>
                    </form>
                </DialogDescription>
            </DialogContent>
        </Dialog>
    );
}

export default UpdateUser;
